/**
 * Authentication Context
 *
 * Manages JWT token and user authentication state for AI Consensus Bot.
 * Uses secure storage for token persistence.
 *
 * TODO: TECHNICAL DEBT - This is a separate auth system from AuthStore (Zustand)
 * This creates potential desynchronization issues and maintenance overhead.
 *
 * Recommended future refactoring:
 * 1. Create a unified auth hook that uses AuthStore
 * 2. Add AI Consensus Bot specific methods to AuthStore
 * 3. Migrate this context to use the shared auth state
 * 4. Remove this file once AI Consensus Bot is refactored
 *
 * See: docs/AUDIT_AUTH_DECEMBRE_2025.md for details
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { syncSecureStorage } from '../services/secureTokenStorage';

const API_URL = import.meta.env.VITE_AI_CONSENSUS_API_URL || 'http://localhost:3006';

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
}

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (email: string, password: string, name?: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  updateProfile: (data: { name?: string; email?: string; password?: string }) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Using secure storage keys
const TOKEN_KEY = 'ai-consensus-auth-token';
const USER_KEY = 'ai-consensus-auth-user';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load token from secure storage on mount
  useEffect(() => {
    const storedToken = syncSecureStorage.getItem(TOKEN_KEY);
    const storedUser = syncSecureStorage.getItem(USER_KEY);

    if (storedToken && storedUser) {
      setToken(storedToken);
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        // Invalid user data
        syncSecureStorage.removeItem(USER_KEY);
      }
      // Verify token is still valid
      verifyToken(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const verifyToken = async (tokenToVerify: string) => {
    try {
      const response = await fetch(`${API_URL}/api/v1/auth/me`, {
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.data);
        syncSecureStorage.setItem(USER_KEY, JSON.stringify(data.data));
      } else {
        // Token invalid
        syncSecureStorage.removeItem(TOKEN_KEY);
        syncSecureStorage.removeItem(USER_KEY);
        setToken(null);
        setUser(null);
      }
    } catch {
      // Network error - keep token for now
    } finally {
      setIsLoading(false);
    }
  };

  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await fetch(`${API_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setToken(data.data.token);
        setUser(data.data.user);
        syncSecureStorage.setItem(TOKEN_KEY, data.data.token);
        syncSecureStorage.setItem(USER_KEY, JSON.stringify(data.data.user));
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      return { success: false, error: 'Connection error' };
    }
  }, []);

  const register = useCallback(async (email: string, password: string, name?: string) => {
    try {
      const response = await fetch(`${API_URL}/api/v1/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setToken(data.data.token);
        setUser(data.data.user);
        syncSecureStorage.setItem(TOKEN_KEY, data.data.token);
        syncSecureStorage.setItem(USER_KEY, JSON.stringify(data.data.user));
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Registration failed' };
      }
    } catch (error) {
      return { success: false, error: 'Connection error' };
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    syncSecureStorage.removeItem(TOKEN_KEY);
    syncSecureStorage.removeItem(USER_KEY);
  }, []);

  const updateProfile = useCallback(async (data: { name?: string; email?: string; password?: string }) => {
    if (!token) return { success: false, error: 'Not authenticated' };

    try {
      const response = await fetch(`${API_URL}/api/v1/auth/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setUser(result.data);
        syncSecureStorage.setItem(USER_KEY, JSON.stringify(result.data));
        return { success: true };
      } else {
        return { success: false, error: result.error || 'Update failed' };
      }
    } catch (error) {
      return { success: false, error: 'Connection error' };
    }
  }, [token]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token && !!user,
        isLoading,
        login,
        register,
        logout,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Get auth token for API calls
 */
export function getAuthToken(): string | null {
  return syncSecureStorage.getItem(TOKEN_KEY);
}

/**
 * Get auth headers for API calls
 */
export function getAuthHeaders(): Record<string, string> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}
